import {
  Button,
  HStack,
  VStack,
  type ButtonProps,
  type StackProps
} from "@chakra-ui/react"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { motion } from "framer-motion"
import { type ReactNode } from "react"
import { createPortal } from "react-dom"
import DiscardButton from "../form/DiscardButton"
import SubmitButton from "../form/SubmitButton"
import useTranslate from "../lng/useTranslate"
import { useAugmentationPanelFooterTarget } from "./AugmentationPanelFooter"
import {
  AUGMENTATION_PANEL_ACTION_BUTTON_PROPS,
  AUGMENTATION_PANEL_DISCARD_BUTTON_PROPS,
  AUGMENTATION_PANEL_SUBMIT_BUTTON_PROPS
} from "./augmentationPanelStyles"
import useFormSubmissionConfirmation from "./useFormSubmissionConfirmation"

export type AugmentationPanelFormActionState = {
  discardCount?: number
  discardFormState?: (preserve: boolean) => void
  dirty: boolean
  isSubmitting: boolean
  isValid: boolean
  successfulSubmissionCount?: number
}

export type AugmentationPanelFormActionsProps = {
  stack?: StackProps
  form?: AugmentationPanelFormActionState
  disabled?: boolean
  requireDirty?: boolean
  submit: ButtonProps
  discard: ButtonProps
  onDiscard?: () => void
  /**
   * HTML form id for the panel form these actions belong to. Required when
   * actions portal into Drawer.Footer (outside the <form> in the DOM) so
   * type="submit" still targets that form.
   */
  formId?: string
}

/** Shared docked action bar for forms rendered inside an Augmentation panel. */
export default function AugmentationPanelFormActions({
  form,
  disabled = false,
  requireDirty = true,
  submit,
  discard,
  onDiscard,
  stack = {},
  formId
}: AugmentationPanelFormActionsProps) {
  const { t } = useTranslate()
  const footerNode = useAugmentationPanelFooterTarget()
  // Portal when the form provides an id so type="submit" can target it after
  // the actions leave the <form> in the DOM. Without formId, keep the old
  // absolute stack (tests / non-panel hosts).
  const portalTarget = formId ? footerNode : null
  const didSave = useFormSubmissionConfirmation({
    dirty: form?.dirty ?? false,
    enabled: form?.successfulSubmissionCount !== undefined,
    isSubmitting: form?.isSubmitting ?? false,
    successfulSubmissionCount: form?.successfulSubmissionCount ?? 0
  })

  const hasDiscardableChanges = form
    ? form.dirty || (form.discardCount ?? 0) > 0
    : false
  const submitDisabled = form
    ? disabled ||
      form.isSubmitting ||
      !form.isValid ||
      (requireDirty && !form.dirty)
    : disabled
  const discardDisabled = form
    ? disabled || form.isSubmitting || !hasDiscardableChanges
    : disabled

  // When portaled into Drawer.Footer the bar is a flex sibling of Body.
  const actions = (
    <VStack
      align="stretch"
      borderTopColor="blackAlpha.100"
      borderTopWidth="1px"
      gap="5px"
      pb="20px"
      pt="augmentationPanel"
      w="full"
      {...stack}
    >
      {form ? (
        <SubmitButton
          {...AUGMENTATION_PANEL_ACTION_BUTTON_PROPS}
          {...AUGMENTATION_PANEL_SUBMIT_BUTTON_PROPS}
          {...submit}
          form={formId}
          disabled={didSave || (submit.disabled ?? submitDisabled)}
          {...(didSave
            ? {
                bg: "augmentationPanel.text",
                color: "white",
                _disabled: {
                  bg: "augmentationPanel.text",
                  color: "white",
                  cursor: "default",
                  opacity: 1
                }
              }
            : {})}
        >
          <motion.span
            key={didSave ? "saved" : "submit"}
            initial={{ opacity: 0, y: 2 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.12 }}
            style={{ display: "inline-flex" }}
          >
            {didSave ? (
              <HStack gap={1.5}>
                <Pathicon icon="check" />
                {String(t("Saved"))}
              </HStack>
            ) : (
              (submit.children as ReactNode)
            )}
          </motion.span>
        </SubmitButton>
      ) : (
        <Button
          {...AUGMENTATION_PANEL_ACTION_BUTTON_PROPS}
          {...AUGMENTATION_PANEL_SUBMIT_BUTTON_PROPS}
          type="submit"
          variant="solid"
          w="full"
          {...submit}
          form={formId}
          disabled={submit.disabled ?? disabled}
        />
      )}
      {form ? (
        <DiscardButton
          {...AUGMENTATION_PANEL_ACTION_BUTTON_PROPS}
          {...AUGMENTATION_PANEL_DISCARD_BUTTON_PROPS}
          colorPalette="gray"
          variant="surface"
          onDiscard={() => {
            form.discardFormState?.(false)
            onDiscard?.()
          }}
          {...discard}
          disabled={discard.disabled ?? discardDisabled}
        />
      ) : (
        <Button
          {...AUGMENTATION_PANEL_ACTION_BUTTON_PROPS}
          {...AUGMENTATION_PANEL_DISCARD_BUTTON_PROPS}
          colorPalette="gray"
          type="button"
          variant="surface"
          w="full"
          {...discard}
          disabled={discard.disabled ?? disabled}
          onClick={(event) => {
            onDiscard?.()
            discard.onClick?.(event)
          }}
        />
      )}
    </VStack>
  )

  return portalTarget ? createPortal(actions, portalTarget) : actions
}
